import { FC, useContext, useEffect, useState } from "react";
import FormFieldsContainer from "../../components/ui/FormFieldsContainer";
import useImageUploader from "../../components/ui/ImageUploader";
import ColorPicker from "../../components/ui/ColorPicker";
import { Button, Card, FlexContent, Form, FormField, Input, Textarea, TranslatableField } from "@rothoblaas/design-system";
import { translatableFieldProps } from "../../utils/translatableField";
import NotificationDispatch, {
  showErrorNotification,
  showSuccessNotification,
} from "../../context/notificationContext";
import useI18n from "../../hooks/useTranslations";
import { useApi } from "../../http/api";
import { FullCategory, UpsertCategoryNewImageBody, UpsertCategoryRemoveImageBody } from "../../http/types/categories";
import { useCategoriesApi } from "../../http/useCategories";
import { useRolesApi } from "../../http/useRoles";

interface AdminCategoryFormBody {
  name: string | null;
  description: string | null;
  image: string | null;
  color: string | null;

  nameIt: string | null;
  nameDe: string | null;
  nameEs: string | null;
  nameFr: string | null;

  descriptionIt: string | null;
  descriptionDe: string | null;
  descriptionEs: string | null;
  descriptionFr: string | null;
}

interface NewEditAdminCategoryFormProps {
  category?: FullCategory;
  disabled?: boolean;
  isLoading?: boolean;
  onClose?: () => void;
}

export const NewEditAdminCategoryForm: FC<NewEditAdminCategoryFormProps> = ({
  category,
  disabled,
  isLoading,
  onClose,
}: NewEditAdminCategoryFormProps) => {
  const i18n = useI18n();
  const dispatch = useContext(NotificationDispatch);
  const categoriesApi = useCategoriesApi();
  const rolesApi = useRolesApi();
  const api = useApi();

  const [isSubmitting, setIsSubmitting] = useState(false);

  const defaultValues: AdminCategoryFormBody = {
    name: category?.name ?? null,
    nameIt: category?.nameIt ?? null,
    nameDe: category?.nameDe ?? null,
    nameEs: category?.nameEs ?? null,
    nameFr: category?.nameFr ?? null,
    description: category?.description ?? null,
    descriptionIt: category?.descriptionIt ?? null,
    descriptionDe: category?.descriptionDe ?? null,
    descriptionEs: category?.descriptionEs ?? null,
    descriptionFr: category?.descriptionFr ?? null,
    image: category?.image ?? null,
    color: category?.color ?? null,
  };

  const { component, newImage, removeImage, onSave } = useImageUploader({
    defaultImage: category?.image,
    isLoading: false,
    disabled: disabled ?? false,
  });

  const rolesSwr = rolesApi.getAdminRoles();

  const submit = (body: UpsertCategoryRemoveImageBody | UpsertCategoryNewImageBody) => {
    if (!!category) {
      categoriesApi
        .updateAdminCategory(category.code, body)
        .then(() => {
          setIsSubmitting(false);
          onSave();
          dispatch(showSuccessNotification(i18n.translation));
        })
        .catch((err) => {
          dispatch(showErrorNotification(i18n, err));
          setIsSubmitting(false);
        })
        .finally(() => setIsSubmitting(false));
    }
  };

  useEffect(() => {
    rolesSwr.trigger();
  }, []);

  useEffect(() => {
    rolesSwr.error && dispatch(showErrorNotification(i18n, rolesSwr.error));
  }, [rolesSwr.error]);

  const onSubmitForm = () => (formData: AdminCategoryFormBody) => {
    setIsSubmitting(true);

    if (newImage) {
      categoriesApi
        .generateUploadImageUrl(category?.code!, newImage.name, newImage.type)
        .then((signedUrl) => {
          api.uploadFile(signedUrl, newImage).then((res) => {
            if (!res.ok) {
              dispatch(showErrorNotification(i18n, res));
              return;
            }

            const body: UpsertCategoryNewImageBody = {
              name: formData.name ?? "",
              description: formData.description ?? "",
              image: newImage.name,
              color: formData.color ?? "",

              nameIt: formData.nameIt ?? "",
              nameDe: formData.nameDe ?? "",
              nameEs: formData.nameEs ?? "",
              nameFr: formData.nameFr ?? "",

              descriptionIt: formData.descriptionIt ?? "",
              descriptionDe: formData.descriptionDe ?? "",
              descriptionEs: formData.descriptionEs ?? "",
              descriptionFr: formData.descriptionFr ?? "",
            };

            submit(body);
          });
        })
        .catch((err) => {
          dispatch(showErrorNotification(i18n, err));
          setIsSubmitting(false);
        })
        .finally(() => setIsSubmitting(false));
    } else {
      const body: UpsertCategoryRemoveImageBody = {
        name: formData.name ?? "",
        description: formData.description ?? "",
        color: formData.color ?? "",
        removeImage: removeImage,

        nameIt: formData.nameIt ?? "",
        nameDe: formData.nameDe ?? "",
        nameEs: formData.nameEs ?? "",
        nameFr: formData.nameFr ?? "",

        descriptionIt: formData.descriptionIt ?? "",
        descriptionDe: formData.descriptionDe ?? "",
        descriptionEs: formData.descriptionEs ?? "",
        descriptionFr: formData.descriptionFr ?? "",
      };
      submit(body);
    }
  };

  return (
    <FlexContent padding noPaddingTop direction="row">
      <Card shadow>
        <Form<AdminCategoryFormBody>
        submitText={i18n.translation.common.save}
        disabled={disabled || isSubmitting}
        defaultValues={defaultValues}
        secondaryButton={
          onClose ? (
            <Button onClick={onClose} variant="secondary">
              {i18n.translation.common.cancel}
            </Button>
          ) : undefined
        }
        onSubmit={onSubmitForm}
      >
        {({ register, formState: { errors }, reset, watch, setValue }) => {
          useEffect(() => {
            reset(defaultValues);
          }, [category]);

          useEffect(() => {
            register("nameIt");
            register("nameDe");
            register("nameEs");
            register("nameFr");
            register("descriptionIt");
            register("descriptionDe");
            register("descriptionEs");
            register("descriptionFr");
            register("color");
          }, [register]);

          const values = watch();

          return (
            <FormFieldsContainer imageUploader={component}>
              <FormField label={i18n.translation.common.name} error={errors.name} isOptional={false}>
                {({ labelId, isOptional, isInvalid }) => (
                  <TranslatableField
                    {...translatableFieldProps("name", values, setValue, i18n.translation)}
                    dialogTitle={i18n.translation.common.name}
                  >
                    <Input
                      id={labelId}
                      isInvalid={isInvalid}
                      disabled={disabled || isLoading}
                      {...register("name", {
                        required: !isOptional,
                        minLength: 1,
                      })}
                    />
                  </TranslatableField>
                )}
              </FormField>

              <FormField label={i18n.translation.common.description} error={errors.description} isOptional={false}>
                {({ labelId, isOptional, isInvalid }) => (
                  <TranslatableField
                    {...translatableFieldProps("description", values, setValue, i18n.translation)}
                    dialogTitle={i18n.translation.common.description}
                    type="textarea"
                  >
                    <Textarea
                      id={labelId}
                      isInvalid={isInvalid}
                      disabled={disabled || isLoading}
                      {...register("description", {
                        required: !isOptional,
                        minLength: 1,
                      })}
                    />
                  </TranslatableField>
                )}
              </FormField>

              <FormField label="Color" error={errors.color} isOptional>
                {({ labelId, isInvalid }) => (
                  <ColorPicker
                    id={labelId}
                    value={values.color}
                    disabled={disabled || isLoading}
                    isInvalid={isInvalid}
                    onChange={(hex) => setValue("color", hex, { shouldDirty: true, shouldValidate: true })}
                  />
                )}
              </FormField>
            </FormFieldsContainer>
          );
        }}
      </Form>
      </Card>
    </FlexContent>
  );
};
